<template>
  <div class="show_all_content_wrapper">
    <!-- Start:: Main Section -->
    <main>
      <!--  =========== Start:: Filter Form =========== -->
      <div
        class="filter_content_wrapper"
        :class="{ active: filterFormIsActive }"
      >
        <button
          class="filter_toggler"
          @click="filterFormIsActive = !filterFormIsActive"
        >
          <i class="fal fa-times"></i>
        </button>
        <div class="filter_title_wrapper">
          <h5>{{ $t("TITLES.searchBy") }}</h5>
        </div>
        <div class="filter_form_wrapper">
          <form @submit.prevent="submitFilterForm">
            <div class="row justify-content-center align-items-center w-100">
              <base-input
                col="4"
                type="text"
                :placeholder="$t('PLACEHOLDERS.package_name')"
                v-model="filterOptions.package_name"
              />

              <!-- Start:: Start Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_from')"
                v-model.trim="filterOptions.from_date"
              />
              <!-- End:: Start Date Input -->

              <!-- Start:: End Date Input -->
              <base-picker-input
                col="4"
                type="date"
                :placeholder="$t('PLACEHOLDERS.time_to')"
                v-model.trim="filterOptions.to_date"
              />
              <!-- End:: End Date Input -->
            </div>

            <div class="btns_wrapper">
              <button class="submit_btn" :disabled="isWaitingRequest">
                <i class="fal fa-search"></i>
              </button>
              <button
                class="reset_btn"
                type="button"
                :disabled="isWaitingRequest"
                @click="resetFilter"
              >
                <i class="fal fa-redo"></i>
              </button>
            </div>
          </form>
        </div>
      </div>
      <!--  =========== End:: Filter Form =========== -->

      <!--  =========== Start:: Table Title =========== -->
      <div class="table_title_wrapper">
        <div class="title_text_wrapper">
          <h5>
            {{ $t("PLACEHOLDERS.packages_report") }}
          </h5>
          <button
            v-if="!filterFormIsActive"
            class="filter_toggler"
            @click.stop="filterFormIsActive = !filterFormIsActive"
          >
            <i class="fal fa-search"></i>
          </button>
        </div>

        <div class="title_route_wrapper">
          <base-button
            class="mt-0 pdf_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadPdf')"
            @fireClick="downloadPdf"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-pdf"></i>
            </template>
          </base-button>
          <base-button
            class="mt-0 excel_btn"
            styleType="solid_btn"
            :btnText="$t('BUTTONS.downloadExcel')"
            @fireClick="downloadExcelAllData"
            :disabled="excelDownloadBtnIsLoading"
          >
            <template v-slot:btn_icon>
              <i class="fal fa-file-excel"></i>
            </template>
          </base-button>
        </div>
      </div>
      <!--  =========== End:: Table Title =========== -->

      <!--  =========== Start:: Data Table =========== -->
      <v-data-table
        class="thumb"
        :loading="loading"
        :loading-text="$t('TABLES.loadingData')"
        :search="searchValue"
        :headers="tableHeaders"
        :items="tableRows"
        item-class="ltr"
        :items-per-page="paginations.items_per_page"
        hide-default-footer
      >
        <template v-slot:[`item.serial_number`]="{ index }">
          {{
            (paginations.current_page - 1) * paginations.items_per_page +
            index +
            1
          }}
        </template>
        <!-- Start:: No Data State -->
        <template v-slot:no-data>
          {{ noDataMessage }}
        </template>
        <!-- End:: No Data State -->

        <template v-slot:[`item.package_id`]="{ item, index }">
          <div class="table_image_wrapper">
            <h6 class="text-danger" v-if="!item.package_id">
              {{ $t("TABLES.noData") }}
            </h6>
            <p v-else>
              {{
                (paginations.current_page - 1) * paginations.items_per_page +
                index +
                1
              }}
            </p>
          </div>
        </template>

        <template v-slot:body.append>
          <tr class="all-stat total-row">
            <td class="text-center">-</td>
            <td class="text-center">-</td>
            <td class="text-center">-</td>
            <td class="text-center">{{ totals.number_of_subscriptions }}</td>
            <td class="text-center">{{ totals.total_revenue }}</td>
            <td class="text-center">{{ totals.platform_profit }}</td>
            <td class="text-center">{{ totals.total_tax }}</td>
          </tr>
        </template>
      </v-data-table>
      <!--  =========== End:: Data Table =========== -->
    </main>
    <!-- End:: Main Section -->

    <!-- Start:: Pagination -->
    <template>
      <div class="pagination_container text-center mt-3 mb-0">
        <v-pagination
          class="py-0"
          square
          v-model="paginations.current_page"
          :length="paginations.last_page"
          :total-visible="6"
          @input="updateRouterQueryParam($event)"
          :prev-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-right' : 'fal fa-angle-left'
          "
          :next-icon="
            getAppLocale == 'ar' ? 'fal fa-angle-left' : 'fal fa-angle-right'
          "
        />
      </div>
    </template>
    <!-- End:: Pagination -->

    <!-- Start:: Generate PDF Template Content -->
    <vue-html2pdf
      :show-layout="false"
      :float-layout="true"
      :enable-download="true"
      :preview-modal="true"
      :filename="$t('PLACEHOLDERS.packages_report')"
      :pdf-quality="2"
      pdf-format="a4"
      :manual-pagination="false"
      :paginate-elements-by-height="1400"
      pdf-content-width="100%"
      @hasGenerated="$message.success($t('MESSAGES.generatedSuccessfully'))"
      ref="html2Pdf"
    >
      <section slot="pdf-content">
        <div class="pdf_file_content">
          <h3 class="file_title">
            {{ $t("PLACEHOLDERS.packages_report") }}
          </h3>
          <v-simple-table>
            <template v-slot:default>
              <thead>
                <tr>
                  <th
                    v-for="(header, index) in tableHeaders"
                    :key="header.value"
                  >
                    <span>{{ header.text }}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in tableRows" :key="row.package_id">
                  <td>
                    {{
                      (paginations.current_page - 1) *
                        paginations.items_per_page +
                      index +
                      1
                    }}
                  </td>
                  <td>{{ row.package_id }}</td>
                  <td>{{ row.package_name }}</td>
                  <td>{{ row.number_of_subscriptions }}</td>
                  <td>{{ row.total_revenue }}</td>
                  <td>{{ row.platform_profit }}</td>
                  <td>{{ row.total_tax }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <!-- ==== Start:: Overall_statistics Addresses ==== -->
          <div class="table_content mt-5">
            <v-simple-table class="stat-table">
              <template v-slot:default>
                <thead class="all-stat">
                  <tr>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_number_of_subscriptions") }}
                    </th>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_revenue") }}
                    </th>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_platform_profit") }}
                    </th>
                    <th class="text-center" style="color: white !important">
                      {{ $t("PLACEHOLDERS.total_tax") }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr class="text-center">
                    <td>{{ totals.number_of_subscriptions }}</td>
                    <td>{{ totals.total_revenue }}</td>
                    <td>{{ totals.platform_profit }}</td>
                    <td>{{ totals.total_tax }}</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </div>
          <!-- ==== End:: Overall_statistics Addresses ==== -->
        </div>
      </section>
    </vue-html2pdf>
    <!-- End:: Generate PDF Template Content -->
  </div>
</template>

<script>
import VueHtml2pdf from "vue-html2pdf";
import { mapGetters } from "vuex";
import * as XLSX from "xlsx";

export default {
  name: "AllFinancialReports",

  components: {
    VueHtml2pdf,
  },

  data() {
    return {
      loading: false,
      isWaitingRequest: false,
      excelDownloadBtnIsLoading: false,
      searchValue: "",

      filterFormIsActive: false,
      filterOptions: {
        package_name: null,
        from_date: null,
        to_date: null,
      },
      tableHeaders: [
        {
          text: this.$t("TABLES.Admins.serialNumber"),
          value: "serial_number",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.package_number"),
          value: "package_id",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.package_name"),
          value: "package_name",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.number_of_subscriptions"),
          value: "number_of_subscriptions",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_revenue"),
          value: "total_revenue",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_platform_profit"),
          value: "platform_profit",
          align: "center",
          sortable: false,
        },
        {
          text: this.$t("PLACEHOLDERS.total_tax"),
          value: "total_tax",
          align: "center",
          sortable: false,
        },
      ],
      tableRows: [],
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 6,
      },
      totals: {
        number_of_subscriptions: 0,
        total_revenue: 0,
        platform_profit: 0,
        total_tax: 0,
      },
    };
  },

  computed: {
    ...mapGetters({
      getAppLocale: "AppLangModule/getAppLocale",
    }),
    hasActiveFilters() {
      return Boolean(
        this.filterOptions.package_name ||
          this.filterOptions.from_date ||
          this.filterOptions.to_date
      );
    },
    noDataMessage() {
      return this.hasActiveFilters
        ? this.$t("TABLES.noMatchingResults")
        : this.$t("TABLES.noReports");
    },
  },

  methods: {
    async submitFilterForm() {
      this.setTableRows();
    },
    async resetFilter() {
      this.filterOptions.package_name = null;
      this.filterOptions.from_date = null;
      this.filterOptions.to_date = null;
      this.setTableRows();
    },
    async setTableRows() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "financial-reports",
          params: {
            page: this.paginations.current_page,
            name: this.filterOptions.package_name,
            from: this.filterOptions.from_date,
            to: this.filterOptions.to_date,
          },
        });
        this.loading = false;
        this.tableRows = res.data.data?.data || [];
        this.totals.number_of_subscriptions =
          res.data.data?.total?.number_of_subscriptions || 0;
        this.totals.total_revenue = res.data.data?.total?.total_revenue || 0;
        this.totals.platform_profit =
          res.data.data?.total?.platform_profit || 0;
        this.totals.total_tax = res.data.data?.total?.total_tax || 0;
        this.paginations.last_page = res.data.data?.meta?.last_page || 1;
        this.paginations.items_per_page = res.data.data?.meta?.per_page || 15;
      } catch (error) {
        this.loading = false;
        console.log(error.response?.data?.message || error.message);
      }
    },
    async downloadPdf() {
      await this.$refs.html2Pdf.generatePdf();
    },
    async downloadExcelAllData() {
      this.loading = true;
      try {
        let res = await this.$axios({
          method: "GET",
          url: "financial-reports",
          params: {
            name: this.filterOptions.package_name,
            from: this.filterOptions.from_date,
            to: this.filterOptions.to_date,
          },
        });
        const allData = res.data.data?.data || [];

        // Map data with translated headers
        const translatedData = allData.map((row, index) => {
          if (this.getAppLocale == "ar") {
            return {
              [this.$t("PLACEHOLDERS.total_tax")]: row.total_tax,
              [this.$t("PLACEHOLDERS.total_platform_profit")]:
                row.platform_profit,
              [this.$t("PLACEHOLDERS.total_revenue")]: row.total_revenue,
              [this.$t("PLACEHOLDERS.number_of_subscriptions")]:
                row.number_of_subscriptions,
              [this.$t("PLACEHOLDERS.package_name")]: row.package_name,
              [this.$t("PLACEHOLDERS.package_number")]: row.package_id,
              [this.$t("TABLES.Admins.serialNumber")]: index + 1,
            };
          } else {
            return {
              [this.$t("TABLES.Admins.serialNumber")]: index + 1,
              [this.$t("PLACEHOLDERS.package_number")]: row.package_id,
              [this.$t("PLACEHOLDERS.package_name")]: row.package_name,
              [this.$t("PLACEHOLDERS.number_of_subscriptions")]:
                row.number_of_subscriptions,
              [this.$t("PLACEHOLDERS.total_revenue")]: row.total_revenue,
              [this.$t("PLACEHOLDERS.total_platform_profit")]:
                row.platform_profit,
              [this.$t("PLACEHOLDERS.total_tax")]: row.total_tax,
            };
          }
        });

        translatedData.push({});
        if (this.getAppLocale === "ar") {
          // Append overall statistics at the bottom
          translatedData.push({
            [this.$t("PLACEHOLDERS.package_name")]: this.$t(
              "PLACEHOLDERS.total_number_of_subscriptions"
            ),
            [this.$t("PLACEHOLDERS.number_of_subscriptions")]:
              this.totals.number_of_subscriptions,
          });

          translatedData.push({
            [this.$t("PLACEHOLDERS.package_name")]: this.$t(
              "PLACEHOLDERS.total_revenue"
            ),
            [this.$t("PLACEHOLDERS.number_of_subscriptions")]:
              this.totals.total_revenue,
          });

          translatedData.push({
            [this.$t("PLACEHOLDERS.package_name")]: this.$t(
              "PLACEHOLDERS.total_platform_profit"
            ),
            [this.$t("PLACEHOLDERS.number_of_subscriptions")]:
              this.totals.platform_profit,
          });

          translatedData.push({
            [this.$t("PLACEHOLDERS.package_name")]: this.$t(
              "PLACEHOLDERS.total_tax"
            ),
            [this.$t("PLACEHOLDERS.number_of_subscriptions")]:
              this.totals.total_tax,
          });
        } else {
          // Append overall statistics at the bottom
          translatedData.push({
            [this.$t("TABLES.Admins.serialNumber")]: this.$t(
              "PLACEHOLDERS.total_number_of_subscriptions"
            ),
            [this.$t("PLACEHOLDERS.package_number")]:
              this.totals.number_of_subscriptions,
          });

          translatedData.push({
            [this.$t("TABLES.Admins.serialNumber")]: this.$t(
              "PLACEHOLDERS.total_revenue"
            ),
            [this.$t("PLACEHOLDERS.package_number")]: this.totals.total_revenue,
          });

          translatedData.push({
            [this.$t("TABLES.Admins.serialNumber")]: this.$t(
              "PLACEHOLDERS.total_platform_profit"
            ),
            [this.$t("PLACEHOLDERS.package_number")]:
              this.totals.platform_profit,
          });

          translatedData.push({
            [this.$t("TABLES.Admins.serialNumber")]: this.$t(
              "PLACEHOLDERS.total_tax"
            ),
            [this.$t("PLACEHOLDERS.package_number")]: this.totals.total_tax,
          });
        }

        const worksheet = XLSX.utils.json_to_sheet(translatedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Reports");

        // Generate Excel file
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        const data = new Blob([excelBuffer], {
          type: "application/octet-stream",
        });
        const url = window.URL.createObjectURL(data);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `${this.$t("PLACEHOLDERS.packages_report")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (error) {
        console.log(error.response?.data?.message || error.message);
      } finally {
        this.loading = false;
      }
    },

    updateRouterQueryParam(pagenationValue) {
      this.$router.push({
        query: {
          ...this.$route.query,
          page: pagenationValue,
        },
      });

      // Scroll To Screen's Top After Get Data
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
  },

  watch: {
    ["$route.query.page"](newPage) {
      const page = +newPage || 1;
      if (page !== this.paginations.current_page) {
        this.paginations.current_page = page;
      }
      this.setTableRows();
    },
  },

  created() {
    if (this.$route.query.page) {
      this.paginations.current_page = +this.$route.query.page;
    }
    this.setTableRows();
  },
};
</script>

<style scoped>
.ex-btn-s {
  color: black !important;
  font-size: 20px;
  font-weight: bold;
  text-decoration: underline !important;
}

.all-stat {
  background: var(--main_theme_clr);
}

.all-stat th {
  color: white !important;
  font-size: 16px !important;
  font-weight: 800;
}

.all-stat.total-row td {
  color: #fff !important;
  font-weight: 700;
}

.excel {
  background: darkgreen;
  padding: 7px 10px;
  border-radius: 12px;
  color: #fff;
  cursor: pointer;
}
</style>
